import { useCallback, useState } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material"
import { useDialogFormState } from "../../hooks/use-dialog-form-state"

export interface SaveAsNewDialogProps {
  open: boolean
  onClose: () => void
  // Resolves on success (dialog closes); rejects to keep the dialog open with the inline error.
  onSave: (name: string) => Promise<void>
}

export default function SaveAsNewDialog({ open, onClose, onSave }: SaveAsNewDialogProps) {
  const [name, setName] = useState("")
  const {
    isBusy: isSaving,
    error,
    beginBusy,
    endBusy,
    reset: resetFormState,
    guardedClose,
  } = useDialogFormState()

  const reset = useCallback(() => {
    setName("")
    resetFormState()
  }, [resetFormState])

  const handleClose = useCallback(() => {
    guardedClose(() => { reset(); onClose() })
  }, [guardedClose, reset, onClose])

  const trimmedName = name.trim()
  const isSaveDisabled = trimmedName.length === 0 || isSaving

  const handleSave = useCallback(async () => {
    if (trimmedName.length === 0) return
    if (!beginBusy()) return
    try {
      await onSave(trimmedName)
      reset()
      onClose()
    } catch {
      // Keep the dialog open so the user can retry without re-typing the name.
      endBusy("Couldn't save the layout. Please try again.")
    }
  }, [trimmedName, beginBusy, onSave, reset, onClose, endBusy])

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Save as new layout</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          required
          fullWidth
          margin="dense"
          label="Layout name"
          value={name}
          onChange={(event) => { setName(event.target.value) }}
          error={error !== null}
          helperText={error ?? undefined}
          disabled={isSaving}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              void handleSave()
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>Cancel</Button>
        <Button onClick={() => { void handleSave() }} disabled={isSaveDisabled} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
